import { useState, useRef, useEffect } from "react";
import { useAttribution } from "@/hooks/useAttribution";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { MessageCircle, Share2, ThumbsUp, ThumbsDown, MapPin, Bookmark, ShoppingCart, Crown, Utensils, Calendar, Gift, ChefHat, ChevronLeft, ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { KudozIcon } from "@/components/KudozIcon";
import { CommentsSheet } from "./CommentsSheet";
import ShareReviewModal from "./ShareReviewModal";
import OrderInitiationModal from "./OrderInitiationModal";
import { cn, resolveEatzyMediaUrl } from "@/lib/utils";
import { LazyImage } from "@/components/LazyImage";
import { AuthenticatedVideo } from "@/components/AuthenticatedVideo";
import { useRestaurantBookmark, useIsRestaurantBookmarked } from "@/hooks/useRestaurantBookmark";
import { useToast } from "@/hooks/use-toast";

interface PromotionDetails {
  type?: 'event' | 'promotion' | 'menu_update' | 'chef_special';
  title?: string;
  couponCode?: string;
  expiresAt?: string;
  eventDate?: string;
}

interface ItemType {
  name: string;
  description?: string;
  price?: number;
  image?: string;
  itemRating?: 'loved' | 'fine' | 'disliked';
  itemRatingText?: string;
  averageRating?: number;
  totalRatings?: number;
  isCrowdFavorite?: boolean;
  isTheBest?: boolean;
}

interface RestaurantPostCardProps {
  id: string;
  restaurant: {
    id: string;
    name: string;
    location: string;
    overallRating: number;
    totalReviews: number;
    logo?: string;
    orderingEnabled?: boolean;
  };
  text: string;
  images?: string[];
  image?: string;
  video?: string;
  mediaUrl?: string;
  mediaType?: string;
  items?: ItemType[];
  createdAt: string;
  likes: number;
  comments: number;
  helpfulUpVotes?: number;
  helpfulDownVotes?: number;
  helpfulVotes?: number;
  tags: string[];
  promotionDetails?: PromotionDetails;
  featured?: boolean;
  isLiked?: boolean;
  isHelpful?: boolean | null;
  isBookmarked?: boolean;
}

const KB_PRESETS = [
  { name: 'kb-pan-right',   duration: 8 },
  { name: 'kb-pan-left',    duration: 8 },
  { name: 'kb-pan-down',    duration: 7 },
  { name: 'kb-pan-up',      duration: 7 },
  { name: 'kb-zoom-in',     duration: 9 },
  { name: 'kb-zoom-out',    duration: 9 },
  { name: 'kb-zoom-pan-tr', duration: 10 },
  { name: 'kb-zoom-pan-bl', duration: 10 },
];

const promotionIcons = {
  event: Calendar,
  promotion: Gift,
  menu_update: Utensils,
  chef_special: ChefHat,
};

const promotionLabels = {
  event: 'Upcoming Event',
  promotion: 'Special Offer',
  menu_update: 'New on Menu',
  chef_special: "Chef's Special",
};

function ItemInfoOverlay({ item }: { item: ItemType }) {
  const [expanded, setExpanded] = useState(false);
  const description = item.description || '';
  const hasDescription = description.length > 0;

  return (
    <div 
      className="absolute top-3 left-3"
      style={{ maxWidth: 'calc(100% - 120px)' }}
    >
      <div className="bg-black/70 backdrop-blur-sm rounded-md px-3 py-1.5">
        <div className="text-white text-sm font-medium truncate">{item.name}</div>
        {hasDescription && (
          <div className="mt-0.5 flex items-baseline gap-1">
            {expanded ? (
              <span className="text-white/80 text-xs leading-tight">
                {description}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded(false);
                  }}
                  className="ml-1 text-xs text-primary font-medium hover:underline"
                >
                  less
                </button>
              </span>
            ) : (
              <>
                <span className="text-white/80 text-xs truncate flex-1 min-w-0">
                  {description}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded(true);
                  }}
                  className="text-xs text-primary font-medium hover:underline flex-shrink-0"
                >
                  more
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function RestaurantPostCard({ 
  id,
  restaurant, 
  text, 
  images,
  image,
  video,
  mediaUrl,
  mediaType,
  items,
  createdAt, 
  likes, 
  comments, 
  helpfulUpVotes = 0,
  helpfulDownVotes = 0,
  helpfulVotes = 0,
  tags,
  promotionDetails,
  featured = false,
  isLiked = false,
  isHelpful = null,
  isBookmarked = false,
}: RestaurantPostCardProps) {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [helpful, setHelpful] = useState<boolean | null>(isHelpful);
  const [upVotes, setUpVotes] = useState(helpfulUpVotes || helpfulVotes);
  const [downVotes, setDownVotes] = useState(helpfulDownVotes);
  const [showComments, setShowComments] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Attribution tracking: fire post_view event on mount
  const { sendAttributionEvent } = useAttribution();
  useEffect(() => {
    sendAttributionEvent('post_view', 'post', id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Ken Burns: pick a random preset each time the visible image changes
  const kbPresetRef = useRef<{ name: string; duration: number } | null>(null);
  const kbLastIndexRef = useRef<number>(-1);
  if (kbLastIndexRef.current !== currentImageIndex || kbPresetRef.current === null) {
    kbLastIndexRef.current = currentImageIndex;
    kbPresetRef.current = KB_PRESETS[Math.floor(Math.random() * KB_PRESETS.length)];
  }
  const kbStyle = {
    animationName: kbPresetRef.current.name,
    animationDuration: `${kbPresetRef.current.duration}s`,
    animationIterationCount: 'infinite',
    animationDirection: 'alternate' as const,
  };
  
  const { toggleBookmark } = useRestaurantBookmark(restaurant.id);
  const bookmarked = useIsRestaurantBookmarked(restaurant.id);

  // Fetch restaurant status to determine if it's open
  // Pre-fetch for ordering-enabled restaurants so status is ready when modal opens
  const { data: restaurantData, isLoading: restaurantLoading } = useQuery<{ 
    liveStatus?: { open: boolean };
    eatzyTimezone?: string | null;
    hoursJson?: Record<string, unknown> | null;
  }>({
    queryKey: ['/api/restaurants', restaurant.id],
    enabled: restaurant.orderingEnabled !== false
  });
  // Default to undefined while loading to show loading state in modal
  // Don't default to true when data is missing - let the modal handle undefined state
  const isRestaurantOpen = restaurantLoading ? undefined : restaurantData?.liveStatus?.open;

  const handleHelpful = (vote: boolean) => {
    const previousHelpful = helpful;
    
    if (previousHelpful === vote) {
      if (vote) {
        setUpVotes(upVotes - 1);
      } else {
        setDownVotes(downVotes - 1);
      }
      setHelpful(null);
    } else if (previousHelpful === null) {
      if (vote) {
        setUpVotes(upVotes + 1);
      } else {
        setDownVotes(downVotes + 1);
      }
      setHelpful(vote);
    } else {
      if (vote) {
        setDownVotes(downVotes - 1);
        setUpVotes(upVotes + 1);
      } else {
        setUpVotes(upVotes - 1);
        setDownVotes(downVotes + 1);
      }
      setHelpful(vote);
    }
  };

  const handleComment = () => {
    setShowComments(true);
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const handleBookmark = () => {
    toggleBookmark();
    
    if (!bookmarked) {
      toast({
        description: (
          <div className="flex items-center gap-2">
            <Bookmark className="w-4 h-4 fill-current text-kudoz" />
            <span>Saved {restaurant.name}</span>
          </div>
        )
      });
    } else {
      toast({
        description: `Removed ${restaurant.name} from saved`
      });
    }
  };

  const handleOrder = () => {
    setShowOrderModal(true);
  };

  // Handler for order modal proceed
  const handleOrderProceed = (settings: {
    serviceType: 'delivery' | 'takeout';
    timing: 'asap' | 'scheduled';
    scheduledTime?: string;
    location: string;
    coordinates?: { lat: number; lng: number };
  }) => {
    // Build URL with order context parameters - match Restaurant page's expected param names
    const params = new URLSearchParams();
    params.set('serviceType', settings.serviceType);
    params.set('timing', settings.timing);
    params.set('location', settings.location);
    if (settings.scheduledTime) {
      params.set('scheduledTime', settings.scheduledTime);
    }
    if (settings.coordinates) {
      params.set('lat', settings.coordinates.lat.toString());
      params.set('lng', settings.coordinates.lng.toString());
    }
    
    // Navigate to restaurant page with order context
    navigate(`/restaurant/${restaurant.id}?${params.toString()}`);
  };

  // Helper to ensure media URLs are properly prefixed for serving
  const resolveMediaUrl = (url: string | undefined): string | null => {
    if (!url) return null;
    const rewritten = resolveEatzyMediaUrl(url);
    if (rewritten !== url) return rewritten ?? null;
    if (url.startsWith('/api/media/') || url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    if (url.startsWith('public/')) {
      return `/api/media/${url}`;
    }
    return url;
  };

  // Build carousel media: general media first, then item media
  const getPostMedia = (): { url: string; item?: ItemType }[] => {
    const mediaItems: { url: string; item?: ItemType }[] = [];
    
    // Add general media first (from images array or mediaUrl)
    if (images && images.length > 0) {
      images.forEach(img => {
        const resolved = resolveMediaUrl(img);
        if (resolved) mediaItems.push({ url: resolved });
      });
    } else if (mediaUrl) {
      const resolved = resolveMediaUrl(mediaUrl);
      if (resolved) mediaItems.push({ url: resolved });
    }
    
    // Add item-level media
    if (items && items.length > 0) {
      items.forEach(item => {
        const resolved = resolveMediaUrl(item.image);
        if (resolved) mediaItems.push({ url: resolved, item });
      });
    }
    
    // Fallback to legacy fields
    if (mediaItems.length === 0) {
      if (image) {
        const resolved = resolveMediaUrl(image);
        if (resolved) mediaItems.push({ url: resolved });
      }
      if (video) {
        const resolved = resolveMediaUrl(video);
        if (resolved) mediaItems.push({ url: resolved });
      }
    }
    
    return mediaItems;
  };
  
  const postMedia = getPostMedia();
  const hasMultipleImages = postMedia.length > 1;
  // Get current item info if showing item-level media
  const currentMediaItem = postMedia[currentImageIndex];
  const currentItem = currentMediaItem?.item || null;
  
  const PromotionIcon = (promotionDetails?.type ? promotionIcons[promotionDetails.type] : null) ?? Gift;
  const promotionLabel = (promotionDetails?.type ? promotionLabels[promotionDetails.type] : null) ?? 'Update';

  return (
    <Card className="w-full max-w-sm mx-auto mb-4 overflow-hidden hover-elevate" data-testid="restaurant-post-card" data-post-id={id}>
      {/* Condensed Header: Partner Post from Restaurant */}
      <div className="p-3 pb-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {/* Restaurant Logo with Partner Crown */}
            <div className="relative flex-shrink-0">
              {restaurant.logo ? (
                <img src={restaurant.logo} alt={restaurant.name} className="w-8 h-8 rounded-full object-cover ring-2 ring-amber-400" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center ring-2 ring-amber-400">
                  <Crown className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              {/* Partner badge + Restaurant name line */}
              <div className="flex items-center gap-1.5 text-sm">
                <Badge 
                  variant="outline" 
                  className="text-[10px] px-1.5 py-0 h-5 text-amber-700 dark:text-amber-400 border-amber-500 bg-amber-100 dark:bg-amber-900/40 flex items-center gap-0.5 flex-shrink-0 font-semibold"
                  data-testid="partner-badge"
                >
                  <Crown className="w-2.5 h-2.5" />
                  Partner Post
                </Badge>
                <span className="text-muted-foreground">from</span>
                <button
                  onClick={() => navigate(`/restaurant/${restaurant.id}`)}
                  className="hover:underline font-bold truncate"
                  data-testid="link-restaurant-name"
                >
                  {restaurant.name}
                </button>
              </div>
              {/* Location only - partner posts don't show star ratings */}
              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{restaurant.location}</span>
              </div>
            </div>
            
            {/* 2x Kudoz badge inline */}
            <Badge 
              variant="outline" 
              className="text-xs px-1.5 py-0.5 text-kudoz border-kudoz/50 bg-kudoz/10 flex items-center gap-0.5 flex-shrink-0"
              data-testid="kudoz-promo-badge"
            >
              <KudozIcon size="xs" />
              <span className="hidden sm:inline">2x</span>
            </Badge>
          </div>
        </div>
      </div>

      {/* Post Content Section - Promotion title and text */}
      <div className="px-4 pb-3 pt-2">
        <div className="bg-accent/30 rounded-md p-3 border border-accent/50">
          {/* Promotion type badge inline with content */}
          <div className="flex items-center gap-2 mb-1">
            {promotionDetails?.type && (
              <Badge className="text-[10px] px-1.5 py-0 h-5 bg-primary/10 text-primary border-primary/30 flex items-center gap-1">
                <PromotionIcon className="w-2.5 h-2.5" />
                {promotionLabel}
              </Badge>
            )}
            {featured && (
              <Badge className="text-[10px] px-1.5 py-0 h-5 bg-amber-500/10 text-amber-600 border-amber-400/50">
                Featured
              </Badge>
            )}
          </div>
          {promotionDetails?.title && (
            <h4 className="font-bold text-sm mb-1">{promotionDetails.title}</h4>
          )}
          <p className="text-sm text-foreground leading-relaxed">
            {text}
          </p>
          {/* Coupon Code */}
          {promotionDetails?.couponCode && (
            <div className="mt-2 inline-flex items-center gap-2 bg-amber-100 dark:bg-amber-900/30 rounded-md px-3 py-1.5 border border-amber-300 dark:border-amber-700">
              <Gift className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              <span className="text-sm font-bold text-amber-700 dark:text-amber-300">Use code: {promotionDetails.couponCode}</span>
            </div>
          )}
          {/* Event Date */}
          {promotionDetails?.eventDate && (
            <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span>{promotionDetails.eventDate}</span>
            </div>
          )}
        </div>
      </div>

      {/* Image/Video Carousel - 16:9 aspect ratio matching ReviewCard */}
      {postMedia.length > 0 && currentMediaItem && (
        <div className="relative aspect-video bg-muted overflow-visible">
          <div className="w-full h-full overflow-hidden">
            {(currentMediaItem.url.match(/\.(mp4|webm|mov)$/i) || mediaType === 'video') ? (
              <AuthenticatedVideo 
                src={currentMediaItem.url} 
                className="w-full h-full object-cover"
                data-testid="post-video"
                controls
                autoPlay
                muted
                loop
                playsInline
              />
            ) : (
              <LazyImage 
                src={currentMediaItem.url} 
                alt="Restaurant post" 
                className="w-full h-full"
                imgClassName="kb-anim"
                imgStyle={kbStyle}
                data-testid="post-image"
              />
            )}
          </div>
          
          {/* Item name overlay - bottom left, above tags */}
          {currentItem && (
            <ItemInfoOverlay item={currentItem} />
          )}
          
          {/* Image Navigation Arrows */}
          {hasMultipleImages && (
            <>
              {currentImageIndex > 0 && (
                <button
                  onClick={() => setCurrentImageIndex(prev => prev - 1)}
                  className="absolute left-0 top-0 bottom-0 w-20 flex items-center justify-start pl-3 bg-gradient-to-r from-black/20 to-transparent hover:from-black/40 transition-all"
                  data-testid="button-prev-image"
                >
                  <div className="bg-white dark:bg-gray-800 rounded-full p-1.5 shadow-lg">
                    <ChevronLeft className="w-5 h-5 text-gray-900 dark:text-white" />
                  </div>
                </button>
              )}
              
              {currentImageIndex < postMedia.length - 1 && (
                <button
                  onClick={() => setCurrentImageIndex(prev => prev + 1)}
                  className="absolute right-0 top-0 bottom-0 w-20 flex items-center justify-end pr-3 bg-gradient-to-l from-black/20 to-transparent hover:from-black/40 transition-all"
                  data-testid="button-next-image"
                >
                  <div className="bg-white dark:bg-gray-800 rounded-full p-1.5 shadow-lg">
                    <ChevronRight className="w-5 h-5 text-gray-900 dark:text-white" />
                  </div>
                </button>
              )}
              
              {/* Image Indicators */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                {postMedia.map((_, index) => (
                  <button
                    key={index}
                    className={cn(
                      "w-1.5 h-1.5 rounded-full transition-all",
                      index === currentImageIndex 
                        ? "bg-white w-4" 
                        : "bg-white/60 hover:bg-white/80"
                    )}
                    onClick={() => setCurrentImageIndex(index)}
                    data-testid={`image-indicator-${index}`}
                  />
                ))}
              </div>
            </>
          )}
          
          {/* Tags Overlay - Bottom left of image */}
          {tags.length > 0 && (
            <div className="absolute bottom-2 left-2 flex flex-wrap gap-1 max-w-[60%]">
              {tags.slice(0, 3).map((tag, index) => (
                <span 
                  key={index}
                  className="px-2 py-0.5 bg-black/60 backdrop-blur-sm text-white text-xs rounded-full"
                  data-testid={`tag-${tag}`}
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}
          
          {/* Order Button Overlay - Top right of image */}
          {restaurant.orderingEnabled !== false && (
            <Button
              variant="default"
              size="sm"
              onClick={handleOrder}
              className="absolute top-3 right-3 h-9 px-3 shadow-lg bg-primary hover:bg-primary/90 z-30"
              style={{ position: 'absolute', top: '12px', right: '12px' }}
              data-testid="button-order"
            >
              <ShoppingCart className="w-4 h-4 mr-1.5" />
              <span className="text-sm font-medium">Order</span>
            </Button>
          )}
        </div>
      )}

      {/* Order Button Banner - Fallback for posts without images */}
      {postMedia.length === 0 && restaurant.orderingEnabled !== false && (
        <div className="px-3 pb-2">
          <Button
            variant="default"
            size="sm"
            onClick={handleOrder}
            className="w-full h-9"
            data-testid="button-order"
          >
            <ShoppingCart className="w-4 h-4 mr-1.5" />
            <span className="text-sm font-medium">Order from {restaurant.name}</span>
          </Button>
        </div>
      )}

      {/* Compact Action Bar - matching ReviewCard */}
      <div className="p-3 pt-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Helpful - Compact inline */}
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleHelpful(true)}
                className={cn(
                  "p-0 h-auto",
                  helpful === true ? "text-kudoz" : "text-muted-foreground hover:text-foreground"
                )}
                data-testid="button-helpful-up"
              >
                <ThumbsUp className={cn("w-4 h-4", helpful === true && "fill-current")} />
              </Button>
              <span className="text-xs text-muted-foreground">{upVotes}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleHelpful(false)}
                className={cn(
                  "p-0 h-auto",
                  helpful === false ? "text-destructive" : "text-muted-foreground hover:text-foreground"
                )}
                data-testid="button-helpful-down"
              >
                <ThumbsDown className={cn("w-4 h-4", helpful === false && "fill-current")} />
              </Button>
            </div>

            {/* Share */}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleShare}
              className="p-0 h-auto text-muted-foreground hover:text-foreground"
              data-testid="button-share"
            >
              <Share2 className="w-4 h-4" />
            </Button>

            {/* Comment */}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleComment}
              className="flex items-center gap-1 p-0 h-auto text-muted-foreground hover:text-foreground"
              data-testid="button-comment"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="text-xs">{comments}</span>
            </Button>
          </div>
          
          {/* Right side: Save + Timestamp */}
          <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBookmark}
                className={cn(
                  "p-0 h-auto",
                  bookmarked ? "text-kudoz" : "text-muted-foreground hover:text-foreground"
                )}
                data-testid="button-bookmark"
              >
                <Bookmark className={cn(
                  "w-4 h-4 transition-transform",
                  bookmarked && "fill-current scale-110"
                )} />
              </Button>
            <span className="text-xs text-muted-foreground ml-2">{createdAt}</span>
          </div>
        </div>
      </div>

      {/* Comments Sheet */}
      <CommentsSheet
        reviewId={id}
        isOpen={showComments}
        onClose={() => setShowComments(false)}
      />

      {/* Share Modal */}
      <ShareReviewModal
        open={showShareModal}
        onOpenChange={setShowShareModal}
        reviewId={id}
        restaurantName={restaurant.name}
      />

      {/* Order Initiation Modal */}
      <OrderInitiationModal
        isOpen={showOrderModal}
        onClose={() => setShowOrderModal(false)}
        onProceed={handleOrderProceed}
        restaurantId={restaurant.id}
        mode="order"
        isRestaurantOpen={isRestaurantOpen}
        timezone={restaurantData?.eatzyTimezone}
        hoursJson={restaurantData?.hoursJson}
      />
    </Card>
  );
}
